import { DropdownOption } from './types';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from 'assets/svg';
import React, { Fragment, useCallback, useMemo } from 'react';

type Props<T> = {
  placeholder?: string;
  options: DropdownOption<T>[];
  required?: boolean;
  label?: string;
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
};

function DropdownV2<T>({ label, required, value, placeholder, options, onChange, disabled }: Props<T>) {
  const option = useMemo(() => options.find((opt) => opt.value === value) ?? null, [value, options]);
  const onDropdownChange = useCallback(
    (opt: DropdownOption) => {
      onChange(opt.value);
    },
    [onChange]
  );

  return (
    <div>
      {label && (
        <label className="block text-sm mb-2">
          {label}
          {required && (
            <span className="bg-palette-primary inline-block rounded-full w-[6px] h-[6px] align-top ml-1"></span>
          )}
        </label>
      )}
      <Listbox value={option} onChange={onDropdownChange} disabled={disabled}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default h-14 bg-white py-4 px-5 text-left border border-palette-color-border ui-disabled:bg-palette-secondary-light-grey text-palette-secondary ui-disabled:text-palette-color-border group-disabled:bg-palette-secondary-light-grey group-disabled:text-palette-color-border">
            <span className="block truncate">
              {option?.label || <span className="text-palette-secondary-grey">{placeholder}</span>}
            </span>
            <span
              className="pointer-events-none absolute inset-y-0 right-6 flex items-center rotate-180 transition-transform ui-open:rotate-0"
              aria-hidden="true"
            >
              <ChevronDownIcon />
            </span>
          </Listbox.Button>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className="absolute max-h-60 w-full overflow-auto bg-white text-base focus:outline-none drop-shadow-[4px_13px_30px_rgba(0,0,0,0.07)] z-10">
              {options.map((option) => (
                <Listbox.Option
                  key={option.label}
                  className="relative cursor-pointer select-none py-2 pl-10 pr-4 ui-active:bg-palette-color-bg-grey"
                  value={option}
                >
                  <span className="block truncate ui-selected:font-semibold">{option.label}</span>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

export default DropdownV2;
